import { MatchEvent } from 'shared/types/match-event';
import { NativeMarkerResult, RecorderKind, RecorderPosition, VideoMarkerDraft } from 'modules/video-markers/domain/interfaces';

/** Хронометраж матча из строки журнала: тайм и секунды от его начала. */
export interface MatchClockReading {
  period: number;
  seconds: number;
}

/** Общее для всех видов меток: чей матч, какая программа и что она ответила. */
export interface VideoMarkerContext {
  organizerId: number | null;
  matchId: number;
  deviceId: number | null;
  recorder: RecorderKind;
  position: RecorderPosition;
  receivedAt: Date;
  chronologyCommitAt: string | null;
}

/** Событие журнала + позиция программы в момент, когда оно дошло до оверлея. */
export function buildEventMarker(
  event: MatchEvent,
  context: VideoMarkerContext,
  nativeMarker: NativeMarkerResult | null,
): VideoMarkerDraft {
  return {
    ...baseDraft('event', context),
    eventId: event.id,
    eventType: event.type,
    matchPeriod: event.period,
    matchSecond: event.seconds,
    nativeMarker,
  };
}

/** Опорная точка «хронометраж ↔ позиция программы»; нативную метку на неё не ставят. */
export function buildClockMarker(clock: MatchClockReading & { running: boolean }, context: VideoMarkerContext): VideoMarkerDraft {
  return {
    ...baseDraft('clock', context),
    matchPeriod: clock.period,
    matchSecond: clock.seconds,
    clockRunning: clock.running,
  };
}

/** Ручная метка оператора; хронометража может не быть, если журнал матча ещё не прочитан. */
export function buildManualMarker(
  clock: MatchClockReading | null,
  context: VideoMarkerContext,
  nativeMarker: NativeMarkerResult | null,
): VideoMarkerDraft {
  return {
    ...baseDraft('manual', context),
    matchPeriod: clock?.period ?? null,
    matchSecond: clock?.seconds ?? null,
    nativeMarker,
  };
}

function baseDraft(kind: VideoMarkerDraft['kind'], context: VideoMarkerContext): VideoMarkerDraft {
  const { position } = context;

  return {
    kind,
    organizerId: context.organizerId,
    matchId: context.matchId,
    deviceId: context.deviceId,
    recorder: context.recorder,
    eventId: null,
    eventType: null,
    matchPeriod: null,
    matchSecond: null,
    clockRunning: null,
    recordingMs: position.recordingMs,
    recordingPaused: position.recordingPaused,
    recordingFile: position.recordingFile,
    streamingMs: position.streamingMs,
    precisionMs: position.precisionMs,
    clientReceivedAt: context.receivedAt.toISOString(),
    chronologyCommitAt: context.chronologyCommitAt,
    nativeMarker: null,
  };
}
